// Next Diamonds — Variant price preview
// Symmetry's <variant-picker> only updates the displayed price when a COMPLETE
// variant is matched (every option chosen). For partial selections it picks
// the cheapest variant OVERALL — so picking Platinum (without a Size yet) still
// shows the 14K base price. It also strips the "USD" suffix because it writes
// the Liquid `money` filter output (which is currency-symbol-only unless
// `show_currency_code` is enabled in theme settings).
//
// This script listens for `change` events on the <variant-picker>, reads
// what's currently selected, finds the cheapest variant MATCHING the partial
// selection, and rewrites `.price__current` with our "$X,XXX.XX USD" format.
// It runs AFTER Symmetry's own update (change bubbles after handleVariantChange
// completes), so we win the last write. The existing pdp-carat-selector
// MutationObserver then re-applies the carat delta and pdp-price-discount
// re-applies the discount strikethrough.
//
// Selectors target Symmetry's actual markup:
//   <variant-picker>
//     <fieldset class="option-selector" data-selector-type="listed">
//       <legend class="label">Metal</legend>
//       <input type="radio" class="opt-btn js-option" value="Platinum">
//     </fieldset>
//     <div class="option-selector" data-selector-type="dropdown">
//       <custom-select> ... .custom-select__option[aria-selected="true"][data-value="6"]
//     </div>
//   </variant-picker>

(function () {
  'use strict';

  function getHandle() {
    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && canonical.href) {
      var m = canonical.href.match(/\/products\/([a-z0-9-]+)/i);
      if (m) return m[1];
    }
    var m2 = location.pathname.match(/\/products\/([a-z0-9-]+)/i);
    return m2 ? m2[1] : null;
  }

  var handle = getHandle();
  if (!handle) return;

  var productData = null;
  // Persistent selection state (keyed by option name). Updated by ANY of the
  // picker variants we support: <variant-picker>, ND custom dropdown, or a
  // raw <select>. runUpdate() reads this to find the matching variant.
  var selected = {};

  function getOptionIndex(name) {
    var names = productData && productData._optionNames || [];
    for (var i = 0; i < names.length; i++) {
      if (names[i] === name) return i;
    }
    return -1;
  }

  function normaliseOptionName(name) {
    if (!name) return name;
    if (name === 'metal') return 'Metal';
    if (name === 'size') return 'Ring Size';
    return name;
  }

  fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      if (!data) return;
      productData = data;
      // products.js returns options as [{name, values}, ...] objects.
      // Cache the names in declared order so we can map a picker's option
      // index to the matching product.options[i].name.
      productData._optionNames = (data.options || []).map(function (o) {
        return (o && o.name) || String(o);
      });
      // Run once on load in case the URL already has ?variant= and Symmetry
      // pre-selected something before our listener attached.
      runUpdate();
    })
    .catch(function () {});

  function fmtMoney(cents) {
    var dollars = Math.round(cents) / 100;
    return '$' + dollars.toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }) + ' USD';
  }

  // Read the currently selected option values straight from Symmetry's picker.
  // Returns an array aligned with productData._optionNames index order.
  function readSelectedOptions(picker) {
    var selectors = picker.querySelectorAll('.option-selector');
    var out = [];
    selectors.forEach(function (sel) {
      var val = null;
      if (sel.dataset.selectorType === 'dropdown') {
        var opt = sel.querySelector('.custom-select__option[aria-selected="true"]');
        val = opt && opt.dataset.value ? opt.dataset.value : null;
      } else {
        var rad = sel.querySelector('input[type="radio"]:checked');
        val = rad && rad.value ? rad.value : null;
      }
      out.push(val);
    });
    return out;
  }

  // Cheapest variant whose option values match the partial selection. A null
  // in `selectedOptions` is treated as "any" — so picking only Metal=Platinum
  // returns the cheapest Platinum variant.
  function findBestVariant(selectedOptions) {
    if (!productData || !productData.variants) return null;
    var matches = productData.variants.filter(function (v) {
      if (!v.options) return false;
      return selectedOptions.every(function (sel, i) {
        return sel == null || v.options[i] === sel;
      });
    });
    if (!matches.length) return null;
    return matches.reduce(function (cheapest, v) {
      return v.price < cheapest.price ? v : cheapest;
    });
  }

  function findPriceEl() {
    return document.querySelector(
      '.product-price .price .price__current, .price .price__current, .price__current, .price__default .price__current, .price-item--regular'
    );
  }

  function setPriceText(cents) {
    var el = findPriceEl();
    if (!el) return;
    var formatted = fmtMoney(cents);
    var current = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (current === formatted) return;
    el.textContent = formatted;
  }

  // Build the selectedOptions array (aligned with productData._optionNames)
  // by checking: Symmetry's <variant-picker> (if present), our `selected` state
  // object (updated by ND-dropdown clicks), and any URL ?variant=.
  function getSelectedOptions() {
    if (!productData) return [];
    var names = productData._optionNames || [];
    var fromPicker = [];
    var picker = document.querySelector('variant-picker');
    if (picker) fromPicker = readSelectedOptions(picker);
    return names.map(function (name, i) {
      // Picker value wins if present; else fall back to our state object.
      var pickerVal = fromPicker[i];
      if (pickerVal) return pickerVal;
      return selected[name] || null;
    });
  }

  function runUpdate() {
    if (!productData) return;
    var selectedOptions = getSelectedOptions();
    var v = findBestVariant(selectedOptions);
    if (!v) return;
    setPriceText(v.price);
  }

  // Coalesce multiple update requests in the same tick into a single rAF
  // call. Variant clicks fire many listeners (Symmetry's, ours, observers)
  // — each used to schedule its own setTimeout, causing 2-3 visible paints
  // and a perceptible "flicker". Now they all funnel into one paint.
  var updateScheduled = false;
  function scheduleUpdate() {
    if (updateScheduled) return;
    updateScheduled = true;
    requestAnimationFrame(function () {
      updateScheduled = false;
      runUpdate();
    });
  }

  // Listen for `change` events that bubble up through <variant-picker>.
  // Symmetry's own handleVariantChange runs first (it's attached directly to
  // the variant-picker element and dispatches synchronously). Our document-
  // level listener catches the same event after it bubbles past, so we have
  // the final word on `.price__current`.
  document.addEventListener('change', function (e) {
    var picker = e.target.closest('variant-picker');
    if (!picker) return;
    scheduleUpdate();
  });

  // Belt-and-suspenders: also listen for the explicit `on:variant:change`
  // event Symmetry dispatches at the end of handleVariantChange. Fires even
  // when only a partial variant is selected.
  document.addEventListener('on:variant:change', scheduleUpdate);

  // Custom selects don't fire native `change` on the parent until after the
  // dropdown closes — listen on click too so quick clicks register.
  document.addEventListener('click', function (e) {
    var picker = e.target.closest('variant-picker');
    if (!picker) return;
    scheduleUpdate();
  }, true);

  // ND custom dropdown (used on products like Addison whose metal selector is
  // our `.specs__row--select` component instead of Symmetry's variant-picker).
  // Each option button carries `data-option-value` and lives inside a row that
  // has `data-option-name` (or `data-spec-edit`). Click → update `selected`,
  // then re-run the price.
  document.addEventListener('click', function (e) {
    var option = e.target.closest('[data-spec-option]');
    if (!option || option.disabled) return;
    var row = option.closest('[data-option-name], [data-spec-edit]');
    if (!row) return;
    var rawName = row.dataset.optionName || row.dataset.specEdit;
    var optName = normaliseOptionName(rawName);
    var optValue = option.dataset.optionValue || (option.textContent || '').trim();
    if (!optName || !optValue) return;
    selected[optName] = optValue;
    scheduleUpdate();
  }, true);

  // Native <select> change (rare but supported as a fallback for any product
  // that uses a plain dropdown instead of swatches or ND custom dropdown).
  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel || sel.tagName !== 'SELECT') return;
    var group = sel.closest('[data-option-name], [data-spec-edit]');
    if (!group) return;
    var rawName = group.dataset.optionName || group.dataset.specEdit;
    var optName = normaliseOptionName(rawName);
    if (!optName || !sel.value) return;
    selected[optName] = sel.value;
    scheduleUpdate();
  }, true);
})();
